import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import purchaseService from './purchaseService';
import { toast } from 'react-toastify';

const initialState = {
  items: [],
  total: 0,
  page: 1,
  limit: 50,
  sum: 0,
  current: null,
  loading: false,
  error: null,
};

export const fetchPurchases = createAsyncThunk('purchase/list', async (params, thunkAPI) => {
  try {
    return await purchaseService.list(params);
  } catch (err) {
    const msg = err.response?.data?.message || err.message || 'Error listando compras';
    toast.error(msg);
    return thunkAPI.rejectWithValue(msg);
  }
});

export const createPurchase = createAsyncThunk('purchase/create', async (data, thunkAPI) => {
  try {
    const res = await purchaseService.create(data);
    toast.success('Compra creada');
    return res;
  } catch (err) {
    const msg = err.response?.data?.message || err.message || 'Error creando compra';
    toast.error(msg);
    return thunkAPI.rejectWithValue(msg);
  }
});

export const getPurchase = createAsyncThunk('purchase/get', async (id, thunkAPI) => {
  try {
    return await purchaseService.getById(id);
  } catch (err) {
    const msg = err.response?.data?.message || err.message || 'Error obteniendo compra';
    toast.error(msg);
    return thunkAPI.rejectWithValue(msg);
  }
});

export const updatePurchase = createAsyncThunk('purchase/update', async ({ id, data }, thunkAPI) => {
  try {
    const res = await purchaseService.update(id, data);
    toast.success('Compra actualizada');
    return res;
  } catch (err) {
    const msg = err.response?.data?.message || err.message || 'Error actualizando compra';
    toast.error(msg);
    return thunkAPI.rejectWithValue(msg);
  }
});

export const removePurchase = createAsyncThunk('purchase/remove', async (id, thunkAPI) => {
  try {
    await purchaseService.remove(id);
    toast.success('Compra eliminada');
    return id;
  } catch (err) {
    const msg = err.response?.data?.message || err.message || 'Error eliminando compra';
    toast.error(msg);
    return thunkAPI.rejectWithValue(msg);
  }
});

export const addPurchasePayment = createAsyncThunk('purchase/addPayment', async ({ id, data }, thunkAPI) => {
  try {
    const res = await purchaseService.addPayment(id, data);
    toast.success('Pago registrado');
    return res;
  } catch (err) {
    const msg = err.response?.data?.message || err.message || 'Error registrando pago';
    toast.error(msg);
    return thunkAPI.rejectWithValue(msg);
  }
});

const purchaseSlice = createSlice({
  name: 'purchase',
  initialState,
  reducers: {
    reset: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPurchases.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(fetchPurchases.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload.items || [];
        state.total = action.payload.total || 0;
        state.page = action.payload.page || 1;
        state.limit = action.payload.limit || 50;
        state.sum = action.payload.sum || 0;
      })
      .addCase(fetchPurchases.rejected, (state, action) => { state.loading = false; state.error = action.payload; })

      .addCase(createPurchase.fulfilled, (state, action) => {
        state.items.unshift(action.payload);
        state.total += 1;
        state.sum += Number(action.payload.total || 0);
      })

      .addCase(getPurchase.fulfilled, (state, action) => { state.current = action.payload; })

      .addCase(updatePurchase.fulfilled, (state, action) => {
        const idx = state.items.findIndex((p) => p._id === action.payload._id);
        if (idx !== -1) state.items[idx] = action.payload;
        if (state.current && state.current._id === action.payload._id) state.current = action.payload;
      })

      .addCase(removePurchase.fulfilled, (state, action) => {
        state.items = state.items.filter((p) => p._id !== action.payload);
        state.total = Math.max(0, state.total - 1);
      })

      .addCase(addPurchasePayment.fulfilled, (state, action) => {
        const idx = state.items.findIndex((p) => p._id === action.payload._id);
        if (idx !== -1) state.items[idx] = action.payload;
        if (state.current && state.current._id === action.payload._id) state.current = action.payload;
      });
  }
});

export const { reset } = purchaseSlice.actions;
export default purchaseSlice.reducer;
